
import React, { useState, useRef } from 'react';
import { 
  FileUp, 
  AlertCircle, 
  CheckCircle2, 
  Database,
  Trash2,
  Play,
  XCircle,
  AlertTriangle,
  Search,
  Info,
  Link,
  ChevronDown,
  ChevronUp,
  FileWarning
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { Lead } from '../types';
import { sendBulkLeadsToN8N } from '../services/n8nService';

interface ValidationResult {
  row: number;
  data: any;
  errors: string[];
  isValid: boolean;
}

interface MappingInfo {
  nameColumn: string;
  numberColumn: string;
}

const BulkUpload: React.FC = () => {
  const [results, setResults] = useState<ValidationResult[]>([]);
  const [mapping, setMapping] = useState<MappingInfo | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);

  const NAME_ALIASES = ['name', 'names', 'full name', 'first name', 'client', 'lead', 'recipient'];
  const NUMBER_ALIASES = ['number', 'phone', 'phone number', 'phone_number', 'mobile', 'cell', 'phone_numbers', 'contact'];

  const validatePhoneNumber = (phone: any) => {
    const phoneStr = String(phone || '').trim();
    const phoneRegex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,12}$/im;
    return phoneRegex.test(phoneStr);
  };

  const findHeaderIndex = (headers: string[], aliases: string[]) => {
    let idx = headers.findIndex(h => aliases.includes(String(h).toLowerCase()));
    if (idx !== -1) return idx;

    return headers.findIndex(h => {
      const normalized = String(h).toLowerCase().replace(/[^a-z0-9]/g, '');
      return aliases.some(alias => {
        const normalizedAlias = alias.replace(/[^a-z0-9]/g, '');
        return normalized.includes(normalizedAlias) || normalizedAlias.includes(normalized);
      });
    });
  };

  const toggleRow = (rowIndex: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowIndex)) {
      newExpanded.delete(rowIndex);
    } else {
      newExpanded.add(rowIndex);
    }
    setExpandedRows(newExpanded);
  };

  const processFile = (file: File) => {
    if (!file) return;
    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
    const isCsv = file.name.endsWith('.csv');

    if (!isExcel && !isCsv) {
      alert("Please upload a valid CSV or Excel (.xlsx) file.");
      return;
    }

    setIsUploading(true);
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        let rowsData: any[][] = [];

        if (isExcel) {
          const data = new Uint8Array(event.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          rowsData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
        } else {
          const content = event.target?.result as string;
          const lines = content.split(/\r?\n/).filter(l => l.trim() !== "");
          rowsData = lines.map(line => line.split(',').map(c => c.trim().replace(/["']/g, "")));
        }

        if (rowsData.length < 2) {
          alert("The file appears to be empty or missing headers.");
          setIsUploading(false);
          return;
        }

        const headers = rowsData[0].map(h => String(h || '').trim());
        const nameIdx = findHeaderIndex(headers, NAME_ALIASES);
        const numberIdx = findHeaderIndex(headers, NUMBER_ALIASES);

        if (nameIdx === -1 || numberIdx === -1) {
          alert(`Column mapping failed.\n\nRequired columns: 'name' and 'number'.\nFound: ${headers.join(', ')}`);
          setIsUploading(false);
          return;
        }

        setMapping({
          nameColumn: headers[nameIdx],
          numberColumn: headers[numberIdx]
        });

        const dataRows = rowsData.slice(1);
        const validationResults: ValidationResult[] = dataRows.map((row, index) => {
          const name = String(row[nameIdx] || "").trim();
          const number = String(row[numberIdx] || "").trim();
          
          const errors: string[] = [];
          if (!name) errors.push("Missing data: Lead Name column is empty.");
          if (!number) {
            errors.push("Missing data: Phone Number column is empty.");
          } else if (!validatePhoneNumber(number)) {
            errors.push(`Invalid syntax: "${number}" does not match E.164 phone standards.`);
          }

          return {
            row: index + 2,
            data: { name, number },
            errors,
            isValid: errors.length === 0
          };
        });

        setTimeout(() => {
          setResults(validationResults);
          setIsUploading(false);
          setExpandedRows(new Set());
        }, 800);
      } catch (err) {
        console.error("Processing error:", err);
        alert("Failed to process the file. Please ensure it is a valid format.");
        setIsUploading(false);
      }
    };

    if (isExcel) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
    
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!isUploading) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (isUploading) return;
    
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleProcessBatch = async () => {
    const validLeads = results
      .filter(r => r.isValid)
      .map(r => ({
        id: crypto.randomUUID(),
        firstName: r.data.name,
        lastName: '',
        phone: r.data.number,
        status: 'pending' as const,
        timestamp: new Date().toISOString()
      }));

    if (validLeads.length === 0) return;
    
    setIsProcessing(true);
    
    try {
      // Uses the default N8N_WEBHOOKS.RETELL_QUEUE URL from n8nService
      await sendBulkLeadsToN8N(validLeads);
      alert(`Success: ${validLeads.length} leads injected into ReachAI queue.`);
      setResults([]);
      setMapping(null);
    } catch (error) {
      alert('Critical Error: n8n Gateway is currently unreachable.');
    } finally {
      setIsProcessing(false);
    }
  };

  const validCount = results.filter(r => r.isValid).length;
  const invalidCount = results.length - validCount;

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="bg-white dark:bg-slate-900 rounded-3xl p-8 border border-slate-200 dark:border-slate-800 shadow-sm transition-colors">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center">
              <Database className="w-6 h-6 mr-3 text-indigo-600" />
              Lead Batch Processor
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mt-1">AI Agent fleet management and high-volume dispatch.</p>
          </div>
          <div className="flex items-center space-x-3">
            {mapping && (
              <div className="hidden lg:flex items-center space-x-2 px-3 py-1.5 bg-indigo-50 dark:bg-indigo-950/40 rounded-xl border border-indigo-100 dark:border-indigo-900 text-[10px] font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-widest animate-in zoom-in-95">
                <Link className="w-3 h-3" />
                <span>Mapped: {mapping.nameColumn} & {mapping.numberColumn}</span>
              </div>
            )}
            {results.length > 0 && (
              <button 
                onClick={() => { setResults([]); setMapping(null); setExpandedRows(new Set()); }}
                className="p-2.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-xl transition-all"
                title="Clear List"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {results.length === 0 ? (
          <div 
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !isUploading && fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-3xl p-16 flex flex-col items-center justify-center transition-all group relative overflow-hidden ${
              isUploading 
                ? 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 cursor-wait' 
                : isDragging
                  ? 'bg-indigo-50/50 dark:bg-indigo-950/20 border-indigo-500 ring-4 ring-indigo-500/10 cursor-copy'
                  : 'border-slate-200 dark:border-slate-800 cursor-pointer hover:border-indigo-400 dark:hover:border-indigo-600 hover:bg-indigo-50/30 dark:hover:bg-indigo-950/10'
            }`}
          >
            {isDragging && (
              <div className="absolute inset-0 bg-indigo-500/5 pointer-events-none flex items-center justify-center animate-in fade-in duration-200">
                <div className="bg-indigo-500 text-white px-4 py-2 rounded-full font-bold text-sm shadow-xl">
                  Drop to Process File
                </div>
              </div>
            )}
            
            <div className={`w-20 h-20 rounded-2xl bg-slate-50 dark:bg-slate-800 flex items-center justify-center transition-colors mb-4 ${!isUploading && 'group-hover:bg-indigo-100 dark:group-hover:bg-indigo-900/30'} ${isDragging ? 'bg-indigo-100 dark:bg-indigo-900 scale-110' : ''}`}>
              <FileUp className={`w-10 h-10 transition-transform ${isUploading ? 'text-indigo-600 animate-bounce' : 'text-slate-400 dark:text-slate-600 group-hover:text-indigo-600 dark:group-hover:text-indigo-400'} ${isDragging ? 'scale-110 rotate-12 text-indigo-600' : ''}`} />
            </div>
            <p className="text-lg font-bold text-slate-700 dark:text-slate-200">
              {isUploading ? 'Mapping Headers...' : isDragging ? 'Ready for Import' : 'Upload Campaign Data'}
            </p>
            <p className="text-slate-400 dark:text-slate-500 mt-2 text-sm text-center max-w-sm">
              Drag and drop or click to select CSV or Excel. <br/>
              Labels like <b>'Phone_Numbers'</b> or <b>'Names'</b> are auto-mapped.
            </p>
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleFileUpload}
              className="hidden" 
              accept=".csv, .xlsx, .xls"
              disabled={isUploading}
            />
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-800 p-5 rounded-2xl">
                <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">Total Leads</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">{results.length}</p>
              </div>
              <div className="bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900 p-5 rounded-2xl">
                <p className="text-[10px] font-bold text-emerald-500 uppercase tracking-wider mb-1">Verified (Ready)</p>
                <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">{validCount}</p>
              </div>
              <div className="bg-rose-50 dark:bg-rose-950/20 border border-rose-100 dark:border-rose-900 p-5 rounded-2xl">
                <p className="text-[10px] font-bold text-rose-500 uppercase tracking-wider mb-1">Errors Found</p>
                <p className="text-3xl font-bold text-rose-600 dark:text-rose-400">{invalidCount}</p>
              </div>
            </div>

            <div className="border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-inner bg-slate-50/20 dark:bg-slate-950/20">
              <div className="max-h-[450px] overflow-y-auto custom-scrollbar">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-white dark:bg-slate-900 sticky top-0 border-b border-slate-200 dark:border-slate-800 z-10">
                    <tr className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                      <th className="px-6 py-4 w-16">Ref</th>
                      <th className="px-6 py-4">Extracted Name</th>
                      <th className="px-6 py-4">Extracted Phone</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4 w-12"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-slate-900">
                    {results.map((res, i) => (
                      <React.Fragment key={i}>
                        <tr 
                          onClick={() => !res.isValid && toggleRow(i)}
                          className={`group transition-colors border-b border-slate-100 dark:border-slate-800 ${
                            !res.isValid ? 'cursor-pointer hover:bg-rose-50/50 dark:hover:bg-rose-950/10' : 'hover:bg-slate-50/50 dark:hover:bg-slate-800/30'
                          } ${expandedRows.has(i) ? 'bg-rose-50/30 dark:bg-rose-950/5' : ''}`}
                        >
                          <td className="px-6 py-4 text-xs font-mono text-slate-300 dark:text-slate-700">#{res.row}</td>
                          <td className="px-6 py-4">
                            <span className={`text-sm font-semibold ${!res.data.name ? 'text-rose-400 italic' : 'text-slate-700 dark:text-slate-300'}`}>
                              {res.data.name || 'Null'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`text-sm font-mono tracking-tighter ${!res.data.number || !validatePhoneNumber(res.data.number) ? 'text-rose-500' : 'text-slate-500 dark:text-slate-400'}`}>
                              {res.data.number || 'Null'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            {res.isValid ? (
                              <div className="flex items-center text-emerald-600 dark:text-emerald-400 text-[10px] font-bold uppercase tracking-wider bg-emerald-50 dark:bg-emerald-950/30 px-2.5 py-1 rounded-lg border border-emerald-100 dark:border-emerald-900 w-fit">
                                <CheckCircle2 className="w-3 h-3 mr-1.5" /> Validated
                              </div>
                            ) : (
                              <div className="flex items-center text-rose-600 dark:text-rose-400 text-[10px] font-bold uppercase tracking-wider bg-rose-100/50 dark:bg-rose-950/40 px-2.5 py-1 rounded-lg border border-rose-200 dark:border-rose-900 w-fit">
                                <FileWarning className="w-3 h-3 mr-1.5" /> 
                                {res.errors.length} {res.errors.length === 1 ? 'Error' : 'Errors'}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 text-right">
                            {!res.isValid && (
                              <button className="text-slate-400 group-hover:text-rose-500 transition-colors">
                                {expandedRows.has(i) ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              </button>
                            )}
                          </td>
                        </tr>
                        {expandedRows.has(i) && (
                          <tr className="bg-rose-50/30 dark:bg-rose-950/5 border-b border-slate-100 dark:border-slate-800 animate-in slide-in-from-top-1 duration-200">
                            <td colSpan={5} className="px-6 py-6">
                              <div className="flex flex-col space-y-3">
                                <div className="flex items-center space-x-2 text-rose-600 dark:text-rose-400 mb-1">
                                  <AlertCircle className="w-4 h-4" />
                                  <span className="text-xs font-bold uppercase tracking-widest">Failure Documentation</span>
                                </div>
                                <div className="grid gap-2">
                                  {res.errors.map((error, errIdx) => (
                                    <div key={errIdx} className="bg-white dark:bg-slate-900/50 p-3 rounded-xl border border-rose-100 dark:border-rose-900/50 flex items-start space-x-3 shadow-sm">
                                      <div className="w-1.5 h-1.5 rounded-full bg-rose-500 mt-1.5 flex-shrink-0" />
                                      <p className="text-sm text-slate-700 dark:text-slate-300 leading-tight">
                                        {error}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                                <div className="pt-2">
                                  <p className="text-[10px] text-slate-400 dark:text-slate-600 uppercase font-bold tracking-widest">
                                    Raw Input Data: <span className="font-mono lowercase text-slate-300 dark:text-slate-700 ml-1">{"{"} name: "{res.data.name}", phone: "{res.data.number}" {"}"}</span>
                                  </p>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex items-center justify-between p-6 bg-slate-900 dark:bg-black rounded-3xl text-white shadow-xl transition-colors">
              <div className="flex items-center space-x-5">
                <div className="p-3 bg-indigo-500 rounded-2xl shadow-lg shadow-indigo-500/20">
                  <Play className="w-6 h-6 fill-current" />
                </div>
                <div>
                  <p className="font-bold text-lg">Dispatch Batch Cluster</p>
                  <p className="text-sm text-slate-400 font-medium">Injecting {validCount} leads into the ReachAI workflow.</p>
                </div>
              </div>
              <button 
                onClick={handleProcessBatch}
                disabled={isProcessing || validCount === 0}
                className="bg-indigo-500 text-white px-10 py-4 rounded-2xl font-bold flex items-center space-x-2 hover:bg-indigo-400 transition-all disabled:opacity-40 disabled:grayscale active:scale-95 shadow-lg shadow-indigo-500/20"
              >
                {isProcessing ? (
                  <div className="flex items-center space-x-3">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Processing...</span>
                  </div>
                ) : (
                  <span>Commit & Run</span>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-slate-900 p-8 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm transition-colors">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-2.5 rounded-xl bg-indigo-50 dark:bg-indigo-950/30 text-indigo-600 dark:text-indigo-400">
              <Link className="w-5 h-5" />
            </div>
            <h4 className="font-bold text-slate-800 dark:text-slate-100">Smart Mapping</h4>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">Our engine automatically maps <b>Phone_Numbers</b>, <b>Mobile</b>, and <b>Names</b> to the internal data schema, reducing pre-processing time by 90%.</p>
        </div>
        <div className="bg-white dark:bg-slate-900 p-8 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm transition-colors">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-2.5 rounded-xl bg-amber-50 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <h4 className="font-bold text-slate-800 dark:text-slate-100">E.164 Pre-Check</h4>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">Numbers are validated against international standards before dispatch to prevent n8n workflow errors and API credit wastage.</p>
        </div>
        <div className="bg-white dark:bg-slate-900 p-8 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm transition-colors">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-2.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400">
              <Info className="w-5 h-5" />
            </div>
            <h4 className="font-bold text-slate-800 dark:text-slate-100">Dispatch Logic</h4>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">Large files are automatically chunked into optimized payloads, ensuring the n8n cluster processes your leads without gateway timeouts.</p>
        </div>
      </div>
    </div>
  );
};

export default BulkUpload;
